import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Label } from "../ui/label";
import { Globe, Loader2 } from "lucide-react";
import { useI18n } from "../../i18n";
import RC6 from "../../i18n/rcweb6Locales";
const API = "/api";

const LABELS = { es: "Español", en: "English", pt: "Português", fr: "Français" };

/** Explicit user locale. Leaving it on "automatic" restores the country-based
 *  fallback exactly as before. RC-WEB-6 PHASE 15: this section is localized
 *  too, so choosing EN/PT/FR leaves no Spanish text behind. */
export const LanguagePreference = () => {
  const { t, setLang } = useI18n();
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/user/locale`)
      .then((r) => setState(r.data))
      .catch(() => setState({ language: null, supported: ["es", "en", "pt", "fr"] }));
  }, []);

  const save = async (value) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/user/locale`, { language: value || null });
      setState((prev) => ({ ...prev, ...r.data }));
      // RC-WEB-6 PHASE 15 — the choice applies in place, without a reload.
      if (value) setLang(value, { persist: false });
      // The confirmation is already shown in the language just chosen.
      const target = RC6[value] || RC6[r.data?.effective_locale];
      toast.success(target?.set?.lang_saved || t("app.set.lang_saved"));
    } catch (_e) {
      toast.error(t("app.set.lang_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;

  return (
    <section aria-labelledby="language-heading"
             className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6"
             data-testid="language-preference-section">
      <h2 id="language-heading" className="text-base md:text-lg font-semibold text-[#0B2545] flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-[#1B84FF]" aria-hidden="true" /> {t("app.set.lang_title")}
      </h2>
      <Label htmlFor="account-language" className="text-sm text-slate-700">
        {t("app.set.lang_desc")}
      </Label>
      <div className="flex items-center gap-3 mt-2">
        <select id="account-language" value={state.language || ""}
                onChange={(e) => save(e.target.value)}
                disabled={saving}
                className="bg-slate-100 border border-slate-300 text-[#0B2545] rounded-lg px-3 py-2 text-sm"
                data-testid="account-language-select">
          <option value="">{t("app.set.lang_auto")}</option>
          {(state.supported || []).map((code) => (
            <option key={code} value={code}>
              {LABELS[code] || code}
            </option>
          ))}
        </select>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[#1B84FF]" aria-hidden="true" />}
      </div>
      <p className="text-xs text-slate-500 mt-2" data-testid="account-language-effective">
        {t("app.set.lang_effective")}: {LABELS[state.effective_locale] || state.effective_locale}
      </p>
    </section>
  );
};

export default LanguagePreference;
